/* ==========================================================================
   POS dialogs — item lookup, quick items, stock & price check, non-barcode
   item, customer, notepad (item notes and bill remarks), recent bills, held
   bills and the shortcut sheet.

   They are plain overlays rather than bootstrap modals: the POS screen is a
   fixed full-viewport surface, and a bootstrap modal would fight it for the
   scroll lock. Every dialog that needs text carries the on-screen keyboard,
   because the till has no physical one.
   ========================================================================== */

import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import Select from "react-select";
import dayjs from "dayjs";
import {
  X,
  Search,
  Grid,
  Package,
  FileText,
  Clock,
  Pause,
  Trash2,
  Printer,
  Tag,
  Hash,
  DollarSign,
  User,
  UserPlus,
  Phone,
  Percent,
} from "feather-icons-react/build/IconComponents";
import OnScreenKeyboard from "./OnScreenKeyboard";
import { num, money, lineTotals } from "./posHelpers";
import { barcodeIncludes } from "../../../core/utils/productBarcode";

const selectStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 11000 }),
  menu: (base) => ({ ...base, zIndex: 11000 }),
};

/* ---------- shell ---------- */

export const POSDialog = ({ icon, title, subtitle, size = "", onClose, footer, dock, headAction, children }) => {
  useEffect(() => {
    const onKey = (event) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        onClose();
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [onClose]);

  return (
    <div className="pos-overlay" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <div className={`pos-dialog ${size ? `pos-dialog--${size}` : ""}`.trim()} role="dialog" aria-label={title}>
        <div className="pos-dialog__head">
          <span className="pos-dialog__icon">{icon}</span>
          <div className="pos-dialog__titles">
            <h5>{title}</h5>
            {subtitle ? <p>{subtitle}</p> : null}
          </div>
          {headAction}
          <button type="button" className="pos-dialog__close" onClick={onClose} aria-label="Close">
            <X />
          </button>
        </div>
        <div className="pos-dialog__body">{children}</div>
        {dock ? <div className="pos-dialog__dock">{dock}</div> : null}
        {footer ? <div className="pos-dialog__foot">{footer}</div> : null}
      </div>
    </div>
  );
};

POSDialog.propTypes = {
  icon: PropTypes.node,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.node,
  size: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  footer: PropTypes.node,
  dock: PropTypes.node,
  headAction: PropTypes.node,
  children: PropTypes.node,
};

/** Show / hide toggle for a dialog's keyboard. */
const KeyboardToggle = ({ open, onToggle }) => (
  <button type="button" className={`pos-linkbtn ${open ? "is-on" : ""}`.trim()} onClick={onToggle}>
    {open ? "Hide keyboard" : "Keyboard"}
  </button>
);

KeyboardToggle.propTypes = {
  open: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
};

/* ---------- shared lookups ---------- */

const priceOf = (product) => num(product?.retailRate ?? product?.basePrice ?? product?.cost);

const matchesSearch = (product, term) => {
  if (!term) return true;
  const needle = term.trim().toLowerCase();
  return (
    `${product?.name ?? ""}`.toLowerCase().includes(needle) ||
    `${product?.code ?? ""}`.toLowerCase().includes(needle) ||
    barcodeIncludes(product, needle)
  );
};

/* ---------- item lookup / quick items ---------- */

export const ItemLookupDialog = ({
  products,
  decimals,
  onPick,
  onClose,
  quick = false,
  initialTerm = "",
  touchMode = true,
}) => {
  const scopeRef = useRef(null);
  const [term, setTerm] = useState(initialTerm);
  const [category, setCategory] = useState("all");
  const [keyboard, setKeyboard] = useState(touchMode && !quick);

  const categories = useMemo(() => {
    const names = new Set();
    (products || []).forEach((product) => {
      const name = product?.category?.name;
      if (name) names.add(name);
    });
    return ["all", ...Array.from(names).sort()];
  }, [products]);

  const shown = useMemo(() => {
    const filtered = (products || []).filter(
      (product) => matchesSearch(product, term) && (category === "all" || product?.category?.name === category)
    );
    // Quick items are for tapping, not browsing: keep the grid to one screenful.
    return quick && !term && category === "all" ? filtered.slice(0, 60) : filtered.slice(0, 300);
  }, [products, term, category, quick]);

  // Enter with a single match rings it up - the same shortcut as the scan bar.
  const submit = () => {
    if (shown.length === 1) onPick(shown[0]);
  };

  return (
    <POSDialog
      icon={quick ? <Grid /> : <Search />}
      title={quick ? "Quick items" : "Item look up"}
      subtitle={quick ? "Tap an item to drop it straight onto the bill" : "Search by name, item code or barcode"}
      onClose={onClose}
      headAction={<KeyboardToggle open={keyboard} onToggle={() => setKeyboard((prev) => !prev)} />}
      dock={keyboard ? <OnScreenKeyboard scopeRef={scopeRef} onSubmit={submit} /> : null}
    >
      <div ref={scopeRef}>
        <div className="pos-search">
          <Search />
          <input
            autoFocus
            type="text"
            inputMode={touchMode ? "none" : undefined}
            value={term}
            onChange={(event) => setTerm(event.target.value)}
            onKeyDown={(event) => event.key === "Enter" && submit()}
            placeholder="Search items…"
          />
        </div>
      </div>

      {categories.length > 1 ? (
        <div className="pos-cats">
          {categories.map((name) => (
            <button
              key={name}
              type="button"
              className={`pos-cat ${category === name ? "is-on" : ""}`.trim()}
              onClick={() => setCategory(name)}
            >
              {name === "all" ? "All items" : name}
            </button>
          ))}
        </div>
      ) : null}

      {shown.length ? (
        <div className="pos-grid">
          {shown.map((product) => (
            <button key={product.guid} type="button" className="pos-prodcard" onClick={() => onPick(product)}>
              <span className="pos-prodcard__name">{product.name}</span>
              <span className="pos-prodcard__code">{product.barcode || product.code || "—"}</span>
              <span className="pos-prodcard__price">{money(priceOf(product), decimals)}</span>
            </button>
          ))}
        </div>
      ) : (
        <p className="pos-empty">No item matches that search.</p>
      )}
    </POSDialog>
  );
};

ItemLookupDialog.propTypes = {
  products: PropTypes.array,
  decimals: PropTypes.number,
  onPick: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  quick: PropTypes.bool,
  initialTerm: PropTypes.string,
  touchMode: PropTypes.bool,
};

/* ---------- stock & price check ---------- */

export const StockPriceCheckDialog = ({ products, taxOf, decimals, onClose, onAdd, touchMode = true }) => {
  const scopeRef = useRef(null);
  const [term, setTerm] = useState("");
  const [picked, setPicked] = useState(null);
  const [keyboard, setKeyboard] = useState(touchMode);

  const matches = useMemo(
    () => (term ? (products || []).filter((product) => matchesSearch(product, term)).slice(0, 40) : []),
    [products, term]
  );

  // Some product feeds carry a live quantity, others do not. Show a figure only
  // when one is actually present rather than implying a stock we cannot see.
  const onHand = picked?.stock ?? picked?.currentStock ?? picked?.quantity ?? null;

  return (
    <POSDialog
      icon={<Package />}
      title="Stock & price check"
      subtitle="Look an item up without touching the bill"
      size="md"
      onClose={onClose}
      headAction={<KeyboardToggle open={keyboard} onToggle={() => setKeyboard((prev) => !prev)} />}
      dock={
        keyboard ? (
          <OnScreenKeyboard scopeRef={scopeRef} onSubmit={() => matches.length === 1 && setPicked(matches[0])} />
        ) : null
      }
      footer={
        <>
          <button type="button" className="pos-btn" onClick={onClose}>
            Close
          </button>
          <button
            type="button"
            className="pos-btn pos-btn--primary"
            disabled={!picked}
            onClick={() => picked && onAdd(picked)}
          >
            Add to bill
          </button>
        </>
      }
    >
      <div ref={scopeRef}>
        <div className="pos-search">
          <Search />
          <input
            autoFocus
            type="text"
            inputMode={touchMode ? "none" : undefined}
            value={term}
            onChange={(event) => {
              setTerm(event.target.value);
              setPicked(null);
            }}
            placeholder="Scan or type a name, code or barcode…"
          />
        </div>
      </div>

      {picked ? (
        <>
          <div className="pos-readout">
            <div className="pos-readout__cell">
              <div className="pos-readout__label">Selling price</div>
              <div className="pos-readout__value">{money(priceOf(picked), decimals)}</div>
            </div>
            <div className="pos-readout__cell">
              <div className="pos-readout__label">Tax</div>
              <div className="pos-readout__value">{money(taxOf(picked?.taxID), 2)}%</div>
            </div>
            <div className="pos-readout__cell">
              <div className="pos-readout__label">On hand</div>
              <div className="pos-readout__value">{onHand === null ? "—" : money(onHand, decimals)}</div>
            </div>
          </div>
          <p className="pos-hint">
            {picked.name}
            {picked?.unit?.name ? ` · sold in ${picked.unit.name}` : ""}
            {picked?.category?.name ? ` · ${picked.category.name}` : ""}
            {onHand === null ? " · stock on hand is not published by the product feed" : ""}
          </p>
        </>
      ) : null}

      {matches.length ? (
        <table className="pos-listtable">
          <thead>
            <tr>
              <th>Item</th>
              <th>Code</th>
              <th className="pos-num">Price</th>
            </tr>
          </thead>
          <tbody>
            {matches.map((product) => (
              <tr
                key={product.guid}
                className={picked?.guid === product.guid ? "is-on" : ""}
                onClick={() => setPicked(product)}
              >
                <td>{product.name}</td>
                <td>{product.barcode || product.code || "—"}</td>
                <td className="pos-num">{money(priceOf(product), decimals)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : !picked ? (
        <p className="pos-hint">Type or scan to find an item, then tap it to see its price and tax.</p>
      ) : null}
    </POSDialog>
  );
};

StockPriceCheckDialog.propTypes = {
  products: PropTypes.array,
  taxOf: PropTypes.func.isRequired,
  decimals: PropTypes.number,
  onClose: PropTypes.func.isRequired,
  onAdd: PropTypes.func.isRequired,
  touchMode: PropTypes.bool,
};

/* ---------- non-barcode item ---------- */

export const ManualItemDialog = ({ products, taxOptions, onSave, onClose, touchMode = true }) => {
  const scopeRef = useRef(null);
  const [product, setProduct] = useState(null);
  const [form, setForm] = useState({ name: "", quantity: "1", price: "", discountPercent: "0", taxGuid: "" });

  const set = (key) => (event) => setForm((prev) => ({ ...prev, [key]: event.target.value }));
  const canSave = Boolean(product) && `${form.name}`.trim() && num(form.quantity) > 0;
  const im = touchMode ? "none" : undefined;
  const selectAll = (event) => event.target.select();

  // Picking the item seeds the row; every field stays editable afterwards.
  const chooseProduct = (option) => {
    setProduct(option);
    const picked = option?.product;
    setForm((prev) => ({
      ...prev,
      name: picked?.name ?? prev.name,
      price: `${priceOf(picked)}`,
      taxGuid: picked?.taxID ?? "",
    }));
  };

  const save = () => {
    if (!canSave) return;
    const tax = taxOptions.find((option) => option.guid === form.taxGuid);
    onSave({
      product: product.value,
      name: `${form.name}`.trim(),
      code: product.product?.code ?? "",
      barcode: product.product?.barcode ?? "",
      unit: product.product?.unit?.guid ?? product.product?.unit ?? null,
      unitName: product.product?.unit?.name ?? "",
      quantity: num(form.quantity, 1),
      price: num(form.price),
      discountPercent: num(form.discountPercent),
      taxGuid: tax?.guid ?? null,
      taxPercent: num(tax?.percentage),
    });
  };

  return (
    <POSDialog
      icon={<Tag />}
      title="Non-barcode item"
      subtitle="Find the item by name, then set the description, price and quantity"
      size="md"
      onClose={onClose}
      dock={<OnScreenKeyboard scopeRef={scopeRef} onSubmit={save} />}
      footer={
        <>
          <button type="button" className="pos-btn" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="pos-btn pos-btn--primary" disabled={!canSave} onClick={save}>
            Add to bill
          </button>
        </>
      }
    >
      <div className="pos-formgrid" ref={scopeRef}>
        <div className="pos-field pos-formgrid__full">
          <span className="pos-field__icon">
            <Search />
          </span>
          <div className="pos-field__box">
            <label className="pos-field__label">Item</label>
            <Select
              className="pos-select"
              classNamePrefix="pos-select"
              options={products}
              value={product}
              placeholder="Pick from the catalogue…"
              menuPortalTarget={document.body}
              styles={selectStyles}
              onChange={chooseProduct}
            />
          </div>
        </div>

        <div className="pos-field pos-formgrid__full">
          <span className="pos-field__icon">
            <FileText />
          </span>
          <div className="pos-field__box">
            <label className="pos-field__label">Description on the bill</label>
            <input type="text" inputMode={im} value={form.name} onChange={set("name")} placeholder="Item description" />
          </div>
        </div>

        <div className="pos-field">
          <span className="pos-field__icon">
            <Hash />
          </span>
          <div className="pos-field__box">
            <label className="pos-field__label">Quantity</label>
            <input type="text" inputMode={im} data-numeric value={form.quantity} onFocus={selectAll} onChange={set("quantity")} />
          </div>
        </div>
        <div className="pos-field">
          <span className="pos-field__icon">
            <DollarSign />
          </span>
          <div className="pos-field__box">
            <label className="pos-field__label">Price</label>
            <input type="text" inputMode={im} data-numeric value={form.price} onFocus={selectAll} onChange={set("price")} />
          </div>
        </div>
        <div className="pos-field">
          <span className="pos-field__icon">
            <Percent />
          </span>
          <div className="pos-field__box">
            <label className="pos-field__label">Discount %</label>
            <input
              type="text"
              inputMode={im}
              data-numeric
              value={form.discountPercent}
              onFocus={selectAll}
              onChange={set("discountPercent")}
            />
          </div>
        </div>
        <div className="pos-field">
          <span className="pos-field__icon">
            <Hash />
          </span>
          <div className="pos-field__box">
            <label className="pos-field__label">Tax</label>
            <select className="pos-input" value={form.taxGuid} onChange={set("taxGuid")}>
              <option value="">No tax</option>
              {taxOptions.map((option) => (
                <option key={option.guid} value={option.guid}>
                  {option.percentage}%
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
    </POSDialog>
  );
};

ManualItemDialog.propTypes = {
  products: PropTypes.array,
  taxOptions: PropTypes.array,
  onSave: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  touchMode: PropTypes.bool,
};

/* ---------- customer ---------- */

export const CustomerDialog = ({ customers, value, mobile, onPick, onAddNew, onClose, touchMode = true }) => {
  const scopeRef = useRef(null);
  const [term, setTerm] = useState("");
  const [phone, setPhone] = useState(mobile || "");
  const im = touchMode ? "none" : undefined;

  const shown = useMemo(() => {
    const needle = term.trim().toLowerCase();
    const list = (customers || []).filter((option) => {
      if (!needle) return true;
      const c = option.customer || {};
      return (
        `${option.label ?? ""}`.toLowerCase().includes(needle) ||
        `${c.mobile ?? ""}`.toLowerCase().includes(needle) ||
        `${c.code ?? ""}`.toLowerCase().includes(needle)
      );
    });
    return list.slice(0, 80);
  }, [customers, term]);

  return (
    <POSDialog
      icon={<User />}
      title="Customer"
      subtitle="Search by name or mobile, or keep the bill as walk-in"
      size="md"
      onClose={onClose}
      dock={<OnScreenKeyboard scopeRef={scopeRef} onSubmit={() => shown.length === 1 && onPick(shown[0], phone)} />}
      footer={
        <>
          <button type="button" className="pos-btn" onClick={onAddNew} style={{ marginRight: "auto" }}>
            <UserPlus />
            New customer
          </button>
          <button type="button" className="pos-btn" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="pos-btn pos-btn--primary" onClick={() => onPick(value, phone)}>
            Done
          </button>
        </>
      }
    >
      <div className="pos-formgrid" ref={scopeRef}>
        <div className="pos-field">
          <span className="pos-field__icon">
            <Search />
          </span>
          <div className="pos-field__box">
            <label className="pos-field__label">Search</label>
            <input
              autoFocus
              type="text"
              inputMode={im}
              value={term}
              onChange={(event) => setTerm(event.target.value)}
              placeholder="Name or mobile"
            />
          </div>
        </div>
        <div className="pos-field">
          <span className="pos-field__icon">
            <Phone />
          </span>
          <div className="pos-field__box">
            <label className="pos-field__label">Mobile on this bill</label>
            <input
              type="text"
              inputMode={im}
              data-numeric
              value={phone}
              onChange={(event) => setPhone(event.target.value)}
              placeholder="Mobile number"
            />
          </div>
        </div>
      </div>

      <div className="pos-custlist">
        <button
          type="button"
          className={`pos-custrow ${!value ? "is-on" : ""}`.trim()}
          onClick={() => onPick(null, phone)}
        >
          <span className="pos-custrow__name">Walk-in customer</span>
          <span className="pos-custrow__sub">{phone || "No customer attached"}</span>
        </button>
        {shown.map((option) => (
          <button
            key={option.value}
            type="button"
            className={`pos-custrow ${value?.value === option.value ? "is-on" : ""}`.trim()}
            onClick={() => onPick(option, option.customer?.mobile || phone)}
          >
            <span className="pos-custrow__name">{option.label}</span>
            <span className="pos-custrow__sub">{option.customer?.mobile || "—"}</span>
          </button>
        ))}
      </div>
    </POSDialog>
  );
};

CustomerDialog.propTypes = {
  customers: PropTypes.array,
  value: PropTypes.object,
  mobile: PropTypes.string,
  onPick: PropTypes.func.isRequired,
  onAddNew: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  touchMode: PropTypes.bool,
};

/* ---------- notepad: item notes and bill remarks ---------- */

export const TextEntryDialog = ({
  title,
  subtitle,
  initial,
  placeholder,
  saveLabel = "Save",
  onSave,
  onClose,
  touchMode = true,
}) => {
  const scopeRef = useRef(null);
  const [text, setText] = useState(initial || "");

  return (
    <POSDialog
      icon={<FileText />}
      title={title}
      subtitle={subtitle}
      size="md"
      onClose={onClose}
      dock={<OnScreenKeyboard scopeRef={scopeRef} enterMode="newline" />}
      footer={
        <>
          <button type="button" className="pos-btn" onClick={() => setText("")} style={{ marginRight: "auto" }}>
            Clear
          </button>
          <button type="button" className="pos-btn" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="pos-btn pos-btn--primary" onClick={() => onSave(text.trim())}>
            {saveLabel}
          </button>
        </>
      }
    >
      <div ref={scopeRef}>
        <textarea
          autoFocus
          className="pos-textarea"
          inputMode={touchMode ? "none" : undefined}
          value={text}
          onChange={(event) => setText(event.target.value)}
          placeholder={placeholder}
        />
      </div>
    </POSDialog>
  );
};

TextEntryDialog.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.node,
  initial: PropTypes.string,
  placeholder: PropTypes.string,
  saveLabel: PropTypes.string,
  onSave: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  touchMode: PropTypes.bool,
};

/* ---------- recent bills ---------- */

export const RecentBillsDialog = ({ bills, loading, decimals, onPrint, onClose }) => (
  <POSDialog
    icon={<Clock />}
    title="Recent bills"
    subtitle="The last bills saved from this branch"
    onClose={onClose}
    footer={
      <button type="button" className="pos-btn" onClick={onClose}>
        Close
      </button>
    }
  >
    {loading ? (
      <p className="pos-empty">Loading bills…</p>
    ) : bills.length ? (
      <table className="pos-listtable">
        <thead>
          <tr>
            <th>Bill no.</th>
            <th>Date</th>
            <th>Customer</th>
            <th className="pos-num">Items</th>
            <th className="pos-num">Total</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {bills.map((bill) => (
            <tr key={bill.guid || bill.entrynumber}>
              <td>
                <strong>{bill.entrynumber}</strong>
              </td>
              <td>{bill.invdate ? dayjs(bill.invdate).format("DD MMM YYYY, hh:mm A") : "—"}</td>
              <td>{bill.customerName || bill?.customer?.name || "Walk-in"}</td>
              <td className="pos-num">{bill?.invSalesDetails?.length ?? 0}</td>
              <td className="pos-num">{money(bill.netTotal, decimals)}</td>
              <td className="pos-num">
                <button type="button" className="pos-linkbtn" onClick={() => onPrint(bill)}>
                  <Printer />
                  Print
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    ) : (
      <p className="pos-empty">No bills yet for this branch.</p>
    )}
  </POSDialog>
);

RecentBillsDialog.propTypes = {
  bills: PropTypes.array,
  loading: PropTypes.bool,
  decimals: PropTypes.number,
  onPrint: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

/* ---------- held bills ---------- */

export const HeldBillsDialog = ({ bills, decimals, onRecall, onDelete, onClose }) => (
  <POSDialog
    icon={<Pause />}
    title="Held bills"
    subtitle="Parked on this terminal — recalling one replaces the current bill"
    size="md"
    onClose={onClose}
    footer={
      <button type="button" className="pos-btn" onClick={onClose}>
        Close
      </button>
    }
  >
    {bills.length ? (
      <table className="pos-listtable">
        <thead>
          <tr>
            <th>Held at</th>
            <th>Customer</th>
            <th className="pos-num">Lines</th>
            <th className="pos-num">Total</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {bills.map((bill) => (
            <tr key={bill.id}>
              <td>{dayjs(bill.heldAt).format("DD MMM, hh:mm A")}</td>
              <td>{bill.customerName || "Walk-in"}</td>
              <td className="pos-num">{bill.lines?.length ?? 0}</td>
              <td className="pos-num">
                {money(
                  (bill.lines || []).reduce((total, line) => total + lineTotals(line).net, 0),
                  decimals
                )}
              </td>
              <td className="pos-num">
                <span className="pos-rowactions">
                  <button type="button" className="pos-linkbtn" onClick={() => onRecall(bill)}>
                    Recall
                  </button>
                  <button
                    type="button"
                    className="pos-iconbtn pos-iconbtn--danger"
                    onClick={() => onDelete(bill.id)}
                    aria-label="Delete held bill"
                  >
                    <Trash2 />
                  </button>
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    ) : (
      <p className="pos-empty">Nothing is on hold.</p>
    )}
  </POSDialog>
);

HeldBillsDialog.propTypes = {
  bills: PropTypes.array,
  decimals: PropTypes.number,
  onRecall: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

/* ---------- shortcut sheet ---------- */

const SHORTCUTS = [
  ["F1", "Shortcut help"],
  ["F2", "Focus the paid amount"],
  ["F3", "Focus product entry"],
  ["F4", "Item look up"],
  ["F6", "Quick items"],
  ["F7", "Hold the bill"],
  ["F8", "Recall a held bill"],
  ["F9", "Save the bill"],
  ["F10", "Save and print"],
  ["Esc", "Clear product entry"],
  ["Del", "Remove the selected line"],
  ["↑ / ↓", "Move between bill lines"],
];

export const ShortcutsDialog = ({ onClose }) => (
  <POSDialog
    icon={<Hash />}
    title="Keyboard shortcuts"
    subtitle="For terminals with a keyboard attached — everything also works by touch"
    size="md"
    onClose={onClose}
    footer={
      <button type="button" className="pos-btn pos-btn--primary" onClick={onClose}>
        Got it
      </button>
    }
  >
    <div className="pos-shortcuts">
      {SHORTCUTS.map(([key, label]) => (
        <div className="pos-shortcuts__row" key={key}>
          <span>{label}</span>
          <span className="pos-shortcuts__key">{key}</span>
        </div>
      ))}
    </div>
  </POSDialog>
);

ShortcutsDialog.propTypes = {
  onClose: PropTypes.func.isRequired,
};
